"use client";

import { Cookie } from "lucide-react";
import * as React from "react";
import { cn } from "#lib/utils.js";
import { Button } from "./button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "./card";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const CookieConsent = /*#__PURE__*/React.forwardRef(({
  variant = "default",
  demo = false,
  onAcceptCallback = () => {},
  onDeclineCallback = () => {},
  className,
  description = "Vi använder cookies för att säkerställa att du får den bästa upplevelsen på vår webbplats. För mer information om hur vi använder cookies, se vår cookie-policy.",
  learnMoreHref = "#",
  ...props
}, ref) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [hide, setHide] = React.useState(false);
  const handleAccept = React.useCallback(() => {
    setIsOpen(false);
    document.cookie = "cookieConsent=true; expires=Fri, 31 Dec 9999 23:59:59 GMT";
    setTimeout(() => {
      setHide(true);
    }, 700);
    onAcceptCallback();
  }, [onAcceptCallback]);
  const handleDecline = React.useCallback(() => {
    setIsOpen(false);
    setTimeout(() => {
      setHide(true);
    }, 700);
    onDeclineCallback();
  }, [onDeclineCallback]);
  React.useEffect(() => {
    try {
      setIsOpen(true);
      if (document.cookie.includes("cookieConsent=true") && !demo) {
        setIsOpen(false);
        setTimeout(() => {
          setHide(true);
        }, 700);
      }
    } catch (error) {
      console.warn("Cookie consent error:", error);
    }
  }, [demo]);
  if (hide) return null;
  const containerClasses = cn("fixed z-50 transition-all duration-700", !isOpen ? "translate-y-full opacity-0" : "translate-y-0 opacity-100", className);
  const commonWrapperProps = {
    ref,
    className: cn(containerClasses, variant === "mini" ? "left-0 right-0 sm:left-4 bottom-4 w-full sm:max-w-3xl" : "bottom-0 left-0 right-0 sm:left-4 sm:bottom-4 w-full sm:max-w-md"),
    ...props
  };
  if (variant === "default") {
    return /*#__PURE__*/_jsx("div", {
      ...commonWrapperProps,
      children: /*#__PURE__*/_jsxs(Card, {
        className: "m-3 shadow-lg",
        children: [/*#__PURE__*/_jsxs(CardHeader, {
          className: "flex flex-row items-center justify-between space-y-0 pb-2",
          children: [/*#__PURE__*/_jsx(CardTitle, {
            className: "text-lg",
            children: "Vi anv\xE4nder cookies"
          }), /*#__PURE__*/_jsx(Cookie, {
            className: "h-5 w-5"
          })]
        }), /*#__PURE__*/_jsxs(CardContent, {
          className: "space-y-2",
          children: [/*#__PURE__*/_jsx(CardDescription, {
            className: "text-sm",
            children: description
          }), /*#__PURE__*/_jsxs("p", {
            className: "text-xs text-muted-foreground",
            children: ["Genom att klicka p\xE5 ", /*#__PURE__*/_jsx("span", {
              className: "font-medium",
              children: "\"Acceptera\""
            }), " godk\xE4nner du v\xE5r anv\xE4ndning av cookies."]
          }), /*#__PURE__*/_jsx("a", {
            href: learnMoreHref,
            className: "text-xs text-primary underline underline-offset-4 hover:no-underline",
            children: "L\xE4s mer"
          })]
        }), /*#__PURE__*/_jsxs(CardFooter, {
          className: "flex gap-2 pt-2",
          children: [/*#__PURE__*/_jsx(Button, {
            onClick: handleDecline,
            variant: "secondary",
            className: "flex-1",
            children: "Avb\xF6j"
          }), /*#__PURE__*/_jsx(Button, {
            onClick: handleAccept,
            className: "flex-1",
            children: "Acceptera"
          })]
        })]
      })
    });
  }
  if (variant === "small") {
    return /*#__PURE__*/_jsx("div", {
      ...commonWrapperProps,
      children: /*#__PURE__*/_jsxs(Card, {
        className: "m-3 shadow-lg",
        children: [/*#__PURE__*/_jsxs(CardHeader, {
          className: "flex flex-row items-center justify-between space-y-0 pb-2 h-0 px-4",
          children: [/*#__PURE__*/_jsx(CardTitle, {
            className: "text-base",
            children: "Vi anv\xE4nder cookies"
          }), /*#__PURE__*/_jsx(Cookie, {
            className: "h-4 w-4"
          })]
        }), /*#__PURE__*/_jsx(CardContent, {
          className: "pt-0 pb-2 px-4",
          children: /*#__PURE__*/_jsx(CardDescription, {
            className: "text-sm",
            children: description
          })
        }), /*#__PURE__*/_jsxs(CardFooter, {
          className: "flex gap-2 h-0 py-2 px-4",
          children: [/*#__PURE__*/_jsx(Button, {
            onClick: handleDecline,
            variant: "secondary",
            size: "sm",
            className: "flex-1 rounded-full",
            children: "Avb\xF6j"
          }), /*#__PURE__*/_jsx(Button, {
            onClick: handleAccept,
            size: "sm",
            className: "flex-1 rounded-full",
            children: "Acceptera"
          })]
        })]
      })
    });
  }
  if (variant === "mini") {
    return /*#__PURE__*/_jsx("div", {
      ...commonWrapperProps,
      children: /*#__PURE__*/_jsx(Card, {
        className: "mx-3 p-0 py-3 shadow-lg",
        children: /*#__PURE__*/_jsxs(CardContent, {
          className: "sm:flex grid gap-4 p-0 px-3.5",
          children: [/*#__PURE__*/_jsx(CardDescription, {
            className: "text-xs sm:text-sm flex-1",
            children: description
          }), /*#__PURE__*/_jsxs("div", {
            className: "flex items-center gap-2 justify-end sm:gap-3",
            children: [/*#__PURE__*/_jsx("a", {
              href: learnMoreHref,
              className: "text-xs text-primary underline underline-offset-4 hover:no-underline mr-2",
              children: "L\xE4s mer"
            }), /*#__PURE__*/_jsxs(Button, {
              onClick: handleDecline,
              size: "sm",
              variant: "secondary",
              className: "text-xs h-7",
              children: ["Avb\xF6j", /*#__PURE__*/_jsx("span", {
                className: "sr-only sm:hidden",
                children: "Avb\xF6j"
              })]
            }), /*#__PURE__*/_jsxs(Button, {
              onClick: handleAccept,
              size: "sm",
              className: "text-xs h-7",
              children: ["Acceptera", /*#__PURE__*/_jsx("span", {
                className: "sr-only sm:hidden",
                children: "Acceptera"
              })]
            })]
          })]
        })
      })
    });
  }
  return null;
});
CookieConsent.displayName = "CookieConsent";
export { CookieConsent };
export default CookieConsent;